import { useState } from "react";
import { Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import type { MemberRole } from "./useWorkspaceMembers";

type Props = {
  onSubmit: (email: string, role: MemberRole) => Promise<boolean>;
  /** Seat-cap summary for display. All optional — pass null when unlimited
   *  on that axis. */
  accountSeatsMax?: number | null;
  accountSeatUsage?: number;
  perWorkspaceMax?: number | null;
  workspaceSeatUsage?: number;
  seatsAvailable?: boolean;
};

export const InviteMemberForm = ({
  onSubmit,
  accountSeatsMax = null,
  accountSeatUsage = 0,
  perWorkspaceMax = null,
  workspaceSeatUsage = 0,
  seatsAvailable = true,
}: Props) => {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<MemberRole>("editor");
  const [inviting, setInviting] = useState(false);

  const handleSend = async () => {
    setInviting(true);
    const ok = await onSubmit(inviteEmail, inviteRole);
    setInviting(false);
    if (ok) setInviteEmail("");
  };

  const showAccount = accountSeatsMax !== null;
  const showWorkspace = perWorkspaceMax !== null;

  return (
    <div className="space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <Label className="text-sm font-medium flex items-center gap-1.5">
          <Mail className="h-4 w-4" /> Invite a teammate
        </Label>
        {(showAccount || showWorkspace) && (
          <p className="text-xs text-muted-foreground">
            {showWorkspace && (
              <span>This workspace: {workspaceSeatUsage} of {perWorkspaceMax}</span>
            )}
            {showWorkspace && showAccount && <span> · </span>}
            {showAccount && (
              <span>Account: {accountSeatUsage} of {accountSeatsMax}</span>
            )}
          </p>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Input
          type="email"
          placeholder="teammate@example.com"
          value={inviteEmail}
          onChange={(e) => setInviteEmail(e.target.value)}
          className="flex-1 min-w-[220px] h-9"
          disabled={inviting || !seatsAvailable}
        />
        <Select value={inviteRole} onValueChange={(v) => setInviteRole(v as MemberRole)} disabled={inviting || !seatsAvailable}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="owner_admin">Owner / admin</SelectItem>
            <SelectItem value="editor">Editor</SelectItem>
            <SelectItem value="viewer">Viewer</SelectItem>
          </SelectContent>
        </Select>
        <Button onClick={handleSend} disabled={inviting || !seatsAvailable} size="sm">
          {inviting ? "Sending…" : "Send invite"}
        </Button>
      </div>
      {!seatsAvailable && (
        <p className="text-xs text-destructive">
          No seats available. Revoke a member or upgrade your plan to invite more teammates.
        </p>
      )}
    </div>
  );
};
